import { MenuItem, Typography, Popover, Select, Link, SelectProps } from "@mui/material";
import { Box } from "@mui/system";
import { GridRenderCellParams } from "@mui/x-data-grid-premium";
import { IAttachment } from "@shared/mirage/core/contracts/IAttachment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface FileTypeCellProps extends GridRenderCellParams {
    onCellChanged: (id: number, field: string, value: string) => Promise<void>;
    readOnly?: boolean;
    onFileTypesRequested?: (folder: string) => string[];
}

const FileTypeCell: React.FC<FileTypeCellProps> = ({ onCellChanged, readOnly, ...params }) => {
    const { id, field, value, colDef, row, onFileTypesRequested } = params;
    const attachmentRow = row as IAttachment;
    const [valueState, setValueState] = useState(value);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [openSelect, setOpenSelect] = useState(false);
    const [fileTypes, setFileTypes] = useState<string[]>([]);
    const wrapperRef = useRef<HTMLDivElement | null>();
    const handleChange = useCallback<NonNullable<SelectProps["onChange"]>>(
        async (event) => {
            const newValue = event.target.value === "none" ? null : event.target.value;
            setValueState(newValue);
            setAnchorEl(null);
            await onCellChanged(Number(id), field as string, newValue as string);
        },
        [field, id, onCellChanged]
    );

    useEffect(() => {
        setValueState(value);
    }, [value]);

    useEffect(() => {
        if (typeof onFileTypesRequested === "function") {
            const fileTypes = onFileTypesRequested(attachmentRow.folder);
            setFileTypes(fileTypes);
        }
    }, [attachmentRow.folder, onFileTypesRequested]);

    const fileTypeOnClick = useCallback((event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setOpenSelect((p) => !p);
    }, []);

    const onClose = () => {
        setOpenSelect(false);
    };

    const options =
        fileTypes.length > 0
            ? ["none", ...fileTypes].map((option) => {
                  return (
                      <MenuItem value={option} key={option}>
                          {option}
                      </MenuItem>
                  );
              })
            : [
                  <MenuItem value={"no-value"} key={"no-value"} disabled>
                      No documents found
                  </MenuItem>,
              ];

    const fullSizeCellSx = {
        width: "100%",
        height: "100%",
    };
    const editableValue = (
        <div style={fullSizeCellSx}>
            <Link component="button" onClick={fileTypeOnClick} variant="body2" underline="hover" sx={{ ...fullSizeCellSx, textAlign: "left" }}>
                {valueState}
            </Link>
            <Popover open={openSelect} anchorEl={anchorEl} onClose={onClose} sx={{ visibility: "hidden" }}>
                <Select
                    open={openSelect}
                    onClose={onClose}
                    value={valueState}
                    onChange={handleChange}
                    sx={{ minWidth: colDef.computedWidth }}
                    MenuProps={{
                        anchorEl,
                        anchorOrigin: { vertical: "top", horizontal: "left" },
                        transformOrigin: {
                            vertical: "top",
                            horizontal: "left",
                        },
                    }}
                >
                    {options}
                </Select>
            </Popover>
        </div>
    );
    if (readOnly) {
        return valueState;
    }
    return (
        <Box component={"div"} ref={wrapperRef} sx={fullSizeCellSx}>
            {editableValue}
        </Box>
    );
};

export default FileTypeCell;
